import React from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRight, AlertCircle, Gift, Monitor, Video } from "lucide-react";
import type { HomeBannersState } from "../hooks/useHomeBanners";
import { TiendaNubeConnectModal } from "./TiendaNubeConnectModal";
import { HomeSetupCard, HomeSetupExito } from "./HomeSetupCard";
import { ProbarEnEscritorioModal } from "./ProbarEnEscritorioModal";
import CuestionarioModal from "./CuestionarioModal";

/**
 * Render de los carteles de home. Es presentacional: recibe el `state` resuelto
 * por `useHomeBanners` (que se llama UNA vez por pantalla) para no duplicar el
 * fetch cuando se rendea en mobile y desktop a la vez.
 *
 * Hoy quedan dos familias:
 *  - Estado de la suscripción (cancelación programada / pausa), que dibuja acá.
 *  - Configuración de la cuenta paga, que delega en `HomeSetupCard` /
 *    `HomeSetupExito` — ADR-2026-08-27-tarjeta-configuracion-cuenta-paga. Este
 *    archivo se queda con el cableado (navegación, modal de Tiendanube) porque el
 *    montaje doble mobile/desktop y sus estados locales ya viven acá.
 */
export const HomeBannersView: React.FC<{
  state: HomeBannersState;
  /** Clases extra para el contenedor (ej. `sm:hidden` / `hidden sm:block`). */
  className?: string;
  /** Quita el margen inferior del banner (para layouts en fila, no apilados). */
  noMargin?: boolean;
  /** Si true, muestra el banner de ESTADO de la suscripción (cancelación
   *  programada / pausada) con prioridad sobre los promocionales. Hoy solo se
   *  pasa en las instancias mobile. */
  showStatus?: boolean;
}> = ({ state, className, noMargin, showStatus }) => {
  const navigate = useNavigate();
  // `freeImagesRemaining` / `creditsTotal` / `discountPercent` siguen en el estado
  // —los consume `CreditsTopBar`, que el llamador monta aparte— pero acá ya no se
  // leen: eran del banner `upsell` (ADR-2026-08-03-barra-creditos-reemplaza-upsell).
  const {
    kind,
    status,
    cancelDate,
    resumeDate,
    cancelTrigger,
    // La necesita el modal de la encuesta, que guarda la respuesta contra la empresa.
    idCompany,
  } = state;

  const mb = noMargin ? "" : "mb-4";
  /* `CTA_ICONO` es el CTA del cartel de la encuesta
     (ADR-2026-08-04-cta-solo-icono-es-cuadrado). Se había ido con el cartel y vuelve
     con él — ADR-2026-09-21-la-encuesta-vuelve-a-la-home-con-piso-de-uso. Los botones
     de estado de suscripción son blancos con borde y el de la configuración es sólido,
     cada uno definido donde se usa. `CTA_SUAVE` (el tratamiento lavanda,
     ADR-2026-08-03-cta-de-carteles-en-tratamiento-suave) queda para "capacitacion" y
     "escritorio", que siguen en la cadena tal cual estaban en principal —
     ADR-2026-09-15-merge-onboarding-usuarios-pagos-a-principal. */
  const CTA_ICONO =
    "inline-flex items-center justify-center gap-1 text-brand-primary hover:text-brand-primary-hover transition-colors";
  const CTA_SUAVE =
    "inline-flex items-center justify-center gap-2 rounded-xl bg-[#EBEFFE] text-brand-primary hover:bg-[#DFE6FD] transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary";
  // Cancelación por rechazo de pago (fondos insuficientes): copy distinto.
  const byPaymentFailure = cancelTrigger === "fondos_insuficientes";

  // Modal Conectar Tiendanube (paso "tiendanube" de la configuración): mismo
  // flujo que Mi marca, sin navegar. Estado local por instancia — la tarjeta se
  // monta dos veces por pantalla y solo la visible recibe el click.
  const [showTnConnect, setShowTnConnect] = React.useState(false);
  const [tnConnecting, setTnConnecting] = React.useState(false);
  const [tnError, setTnError] = React.useState<string | null>(null);
  // Modal "Probá Atlas en tu computadora" (cartel "escritorio"). Estado local por
  // instancia — `HomeBannersView` se rendea dos veces por pantalla (mobile +
  // desktop) y sólo la visible recibe el click.
  const [showEscritorio, setShowEscritorio] = React.useState(false);
  // Modal de la encuesta (cartel "cuestionario"). Mismo patrón: estado local por
  // instancia — `HomeBannersView` se rendea dos veces por pantalla (mobile +
  // desktop) y sólo la visible recibe el click.
  const [showCuestionario, setShowCuestionario] = React.useState(false);
  /* El cartel se baja al CERRAR el modal, no al enviar: `dismissCuestionario` pone
     `kind` en null y este componente hace `return null` sin kind, lo que desmontaría
     el modal ANTES de que el usuario llegue a ver la pantalla de "¡Gracias!". El ref
     evita depender del timing del re-render. */
  const cuestionarioDoneRef = React.useRef(false);
  const handleTnConnect = async () => {
    if (tnConnecting) return;
    setTnConnecting(true);
    setTnError(null);
    try {
      await state.connectTiendanube?.(); // al éxito el hook marca el paso
      setShowTnConnect(false);
    } catch (e) {
      // Antes esto era un `catch` vacío: con la ventana bloqueada el banner
      // quedaba igual y el usuario no se enteraba de nada
      // (ADR-2026-08-07-tiendanube-popup-safari).
      const msg = e instanceof Error ? e.message : "No se pudo conectar.";
      // Cerrar la ventana a propósito no es un error que haya que cantarle.
      setTnError(msg === "Conexión cancelada." ? null : msg);
    } finally {
      setTnConnecting(false);
    }
  };

  // ── Banner de estado de suscripción (prioridad sobre la configuración) ──
  // Cancelación programada (ámbar) — espeja la vista del Paywall.
  if (showStatus && status === "cancelled") {
    return (
      <div className={className}>
        <div
          className={`${mb} bg-amber-50 border border-amber-200 rounded-2xl p-4 relative sm:flex sm:items-center sm:justify-between sm:gap-3`}
        >
          <div className="pb-12 sm:pb-0">
            <div className="flex items-center gap-2 mb-0.5 sm:mb-1">
              <AlertCircle className="w-4 h-4 text-amber-600 shrink-0" />
              <h3 className="text-[14px] font-semibold text-brand-black/90 sm:text-[15px]">
                Cancelación programada
              </h3>
            </div>
            <p className="text-[13px] leading-snug text-[#3C3C43]/60 sm:text-[15px] sm:leading-normal">
              Tu plan se cancelará el{" "}
              <strong className="font-medium text-brand-black/80">
                {cancelDate}
              </strong>
              {byPaymentFailure
                ? " por rechazo de tu último pago."
                : ". No se te realizará ningún cobro adicional."}
            </p>
          </div>
          <button
            type="button"
            onClick={() => navigate("/subscription")}
            className="absolute bottom-4 right-4 sm:relative sm:bottom-auto sm:right-auto inline-flex items-center gap-1.5 h-10 px-4 bg-white border border-neutral-200 text-brand-black/90 rounded-xl shadow-sm text-[13.5px] font-medium hover:bg-neutral-50 transition-colors flex-shrink-0 sm:text-[15px]"
          >
            Ver detalles
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }
  // Suscripción pausada (azul) — espeja la vista del Paywall.
  if (showStatus && status === "paused") {
    return (
      <div className={className}>
        <div
          className={`${mb} bg-blue-50 border border-blue-200 rounded-2xl p-4 relative sm:flex sm:items-center sm:justify-between sm:gap-3`}
        >
          <div className="pb-12 sm:pb-0">
            <div className="flex items-center gap-2 mb-0.5 sm:mb-1">
              <AlertCircle className="w-4 h-4 text-blue-600 shrink-0" />
              <h3 className="text-[14px] font-semibold text-brand-black/90 sm:text-[15px]">
                Suscripción pausada
              </h3>
            </div>
            <p className="text-[13px] leading-snug text-[#3C3C43]/60 sm:text-[15px] sm:leading-normal">
              Tu plan se reanudará el{" "}
              <strong className="font-medium text-brand-black/80">
                {resumeDate}
              </strong>
              . No se te cobrará durante la pausa.
            </p>
          </div>
          <button
            type="button"
            onClick={() => navigate("/subscription")}
            className="absolute bottom-4 right-4 sm:relative sm:bottom-auto sm:right-auto inline-flex items-center gap-1.5 h-10 px-4 bg-white border border-neutral-200 text-brand-black/90 rounded-xl shadow-sm text-[13.5px] font-medium hover:bg-neutral-50 transition-colors flex-shrink-0 sm:text-[15px]"
          >
            Ver detalles
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  if (kind === null) return null;

  /* Acá vivía el banner `upsell` de la prueba gratis, con sus tres lecturas según
     saldo y cupón. Lo reemplazó la barra superior de créditos
     (ADR-2026-08-03-barra-creditos-reemplaza-upsell): decía lo mismo que ella —"te
     quedan N", "te quedaste sin", el % del cupón— en un formato distinto y en la
     misma pantalla.

     Y acá vivían los tres carteles sueltos de la cuenta paga —marca, encuesta,
     Tienda Nube—, que salían de a uno en secuencia (ADR-889). Los dos que quedan
     son ahora la tarjeta de abajo; la encuesta se fue del recorrido
     (ADR-2026-08-27-tarjeta-configuracion-cuenta-paga). `CuestionarioModal` sigue
     en el repo, sin llamador: lo que se dio de baja es su lugar en la home, no la
     encuesta. */

  /* kind === "cuestionario": la encuesta de feedback, con premio de 10 imágenes —
     ADR-2026-09-21-la-encuesta-vuelve-a-la-home-con-piso-de-uso. Quién la ve lo
     decide `useHomeBanners` (cuenta paga + 30 creaciones + sin responder).

     Misma anatomía que los otros carteles de una sola acción: tarjeta blanca con
     borde, ícono + título, y el CTA a la derecha. En mobile queda sólo la flecha
     (ADR-2026-08-04-carteles-mobile-texto-mas-chico-y-cta-igual): el título ya dice
     qué se reclama. */
  if (kind === "cuestionario") {
    return (
      <div className={className}>
        <div
          className={`${mb} bg-white border border-neutral-200 rounded-2xl p-4 relative flex items-center justify-between gap-3`}
        >
          <div className="flex min-w-0 items-center gap-2">
            <Gift className="w-4 h-4 text-brand-primary shrink-0" />
            <h3 className="text-[14px] font-semibold text-brand-black/90 sm:text-[15px]">
              Obtené 10 imágenes gratis extra
            </h3>
          </div>
          <button
            type="button"
            aria-label="Reclamar"
            onClick={() => setShowCuestionario(true)}
            className={`shrink-0 text-[14px] font-medium ${CTA_ICONO}`}
          >
            <span className="hidden sm:inline">Reclamar</span>
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
        {idCompany && (
          <CuestionarioModal
            open={showCuestionario}
            idCompany={idCompany}
            onClose={() => {
              setShowCuestionario(false);
              if (cuestionarioDoneRef.current) state.dismissCuestionario?.();
            }}
            onSubmitted={() => {
              cuestionarioDoneRef.current = true;
            }}
          />
        )}
      </div>
    );
  }

  // kind === "capacitacion": la llamada de capacitación incluida de los planes PRO y
  // ULTRA (ADR-2026-08-25-banner-llamada-capacitacion-ultra,
  // ADR-2026-10-06-la-llamada-de-capacitacion-tambien-es-de-pro). Mismo shell que
  // "escritorio" porque son DOS acciones. Las dos marcan el flag
  // (`dismissCapacitacion`): tocó "Agendar" o "No me interesa" → el cartel no
  // vuelve, y la cadena se reevalúa para que el siguiente aparezca en el acto.
  if (kind === "capacitacion") {
    return (
      <div className={className}>
        <div
          className={`${mb} bg-white border border-neutral-200 rounded-2xl p-4 relative sm:flex sm:items-center sm:justify-between sm:gap-3`}
        >
          <div className="pb-14 sm:pb-0">
            <div className="flex items-center gap-2 mb-0.5 sm:mb-1">
              <Video className="w-4 h-4 text-brand-primary shrink-0" />
              <h3 className="text-[14px] font-semibold text-brand-black/90 sm:text-[15px]">
                Agendá tu videollamada de capacitación
              </h3>
            </div>
            <p className="text-[13px] leading-snug text-[#3C3C43]/60 sm:text-[15px] sm:leading-normal">
              Tu plan incluye una videollamada personalizada para
              sacarle el máximo provecho a Atlas.
            </p>
          </div>
          <div className="absolute bottom-4 left-4 right-4 flex items-center justify-end gap-2 sm:relative sm:bottom-auto sm:left-auto sm:right-auto sm:w-auto">
            <button
              type="button"
              onClick={() => state.dismissCapacitacion?.()}
              className="inline-flex items-center justify-center h-10 px-3 text-[13.5px] font-medium text-[#3C3C43]/60 hover:text-brand-black/80 rounded-xl transition-colors flex-shrink-0 sm:text-[15px]"
            >
              No me interesa
            </button>
            {/* "Agendar" también da el cartel por resuelto ANTES de navegar: la
                pantalla de destino tiene el link del calendar y su propio flujo,
                y si el usuario vuelve sin agendar, insistirle con el cartel que
                ya tocó es ruido (mismo criterio que el modal de escritorio). */}
            <button
              type="button"
              onClick={() => {
                state.dismissCapacitacion?.();
                navigate("/capacitacion-ultra", { state: { desdeInicio: true } });
              }}
              className={`h-10 flex-shrink-0 px-4 text-[13.5px] font-medium sm:text-[15px] ${CTA_SUAVE}`}
            >
              Agendar
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // kind === "escritorio": invitación a usar Atlas en una computadora. Sólo
  // llega acá en viewport de teléfono — el filtro vive en `useHomeBanners` y no
  // en un `sm:hidden` de este render, porque el `kind` es uno solo y esconder por
  // CSS al que ganó la cadena dejaría a los usuarios de escritorio con un hueco
  // invisible (ver el comentario del hook).
  //
  // Mismo shell que "capacitacion" porque son DOS acciones: en
  // mobile no entran en la fila del título, así que van abajo.
  if (kind === "escritorio") {
    return (
      <div className={className}>
        <div
          className={`${mb} bg-white border border-neutral-200 rounded-2xl p-4 relative sm:flex sm:items-center sm:justify-between sm:gap-3`}
        >
          <div className="pb-14 sm:pb-0">
            <div className="flex items-center gap-2 mb-0.5 sm:mb-1">
              <Monitor className="w-4 h-4 text-brand-primary shrink-0" />
              <h3 className="text-[14px] font-semibold text-brand-black/90 sm:text-[15px]">
                Probá Atlas en tu computadora
              </h3>
            </div>
            <p className="text-[13px] leading-snug text-[#3C3C43]/60 sm:text-[15px] sm:leading-normal">
              Vas a tener más modos disponibles y vas a generar más rápido.
            </p>
          </div>
          <div className="absolute bottom-4 left-4 right-4 flex items-center justify-end gap-2 sm:relative sm:bottom-auto sm:left-auto sm:right-auto sm:w-auto">
            <button
              type="button"
              onClick={() => state.dismissEscritorio?.()}
              className="inline-flex items-center justify-center h-10 px-3 text-[13.5px] font-medium text-[#3C3C43]/60 hover:text-brand-black/80 rounded-xl transition-colors flex-shrink-0 sm:text-[15px]"
            >
              No me interesa
            </button>
            <button
              type="button"
              onClick={() => setShowEscritorio(true)}
              className={`h-10 flex-shrink-0 px-4 text-[13.5px] font-medium sm:text-[15px] ${CTA_SUAVE}`}
            >
              Probar
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
        {/* Cerrar la pantalla también da el cartel por visto: ya leyó el mensaje.
            El orden importa — `dismissEscritorio` pone `kind` en null y esta rama
            deja de renderizarse, así que el modal se desmonta con ella. */}
        <ProbarEnEscritorioModal
          open={showEscritorio}
          onClose={() => {
            setShowEscritorio(false);
            state.dismissEscritorio?.();
          }}
        />
      </div>
    );
  }

  // Configuración de la cuenta paga: los pasos que le faltan, con progreso.
  if (kind === "setup") {
    return (
      <div className={className}>
        <HomeSetupCard
          pasos={state.setup ?? []}
          clave={state.idCompany}
          noMargin={noMargin}
          onOmitir={state.omitirPaso}
          onPaso={(id) => {
            if (id === "marca") {
              navigate("/redes-sociales/descripcion-brand");
              return;
            }
            setShowTnConnect(true);
          }}
        />
        {/* companyHasStores={false}: el paso sólo se ofrece si la empresa NO
            tiene NINGUNA tienda conectada (ver useHomeBanners). */}
        <TiendaNubeConnectModal
          open={showTnConnect}
          companyHasStores={false}
          connecting={tnConnecting}
          error={tnError}
          onCancel={() => {
            setTnError(null);
            setShowTnConnect(false);
          }}
          onConnect={handleTnConnect}
        />
      </div>
    );
  }

  // Terminó los pasos: felicitación, una sola vez.
  return (
    <HomeSetupExito
      className={className}
      noMargin={noMargin}
      onCerrar={state.cerrarCelebracion}
    />
  );
};
